import { useMemo } from 'react';
import { Info, OctagonX, Split } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import type { NodeStruct } from '@/types/node-types';
import {
  getValidateConfig,
  validateValidateConfig,
} from '@/pages/workflow/helper/functional-control-flow';
import { DraftInput } from './switch-cases-editor';
import { cn } from '@/lib/utils';

/**
 * "If a check fails": the Validate node's `mode`, as two choices in words.
 * Fail mode also owns the failure code and message (otherwise hidden behind
 * "advanced"); collect mode shows the three outputs it feeds instead.
 */

interface ValidateModeControlProps {
  struct: NodeStruct;
  disabled?: boolean;
  onChange: (fieldId: string, value: string) => void;
}

const OPTIONS = [
  {
    mode: 'fail' as const,
    icon: OctagonX,
    title: 'Stop the run',
    body: 'The run fails here with the code and message below; nothing downstream runs.',
  },
  {
    mode: 'collect' as const,
    icon: Split,
    title: 'Pass the value through',
    body: 'The run continues. Downstream nodes get the value, whether it is valid, and the list of errors.',
  },
];

export function ValidateModeControl({
  struct,
  disabled = false,
  onChange,
}: ValidateModeControlProps) {
  const config = useMemo(() => getValidateConfig(struct), [struct]);
  const errors = useMemo(() => validateValidateConfig(config), [config]);
  const errorAt = (path: string): string | null =>
    errors.find((error) => error.path === path)?.message ?? null;
  const outputNames = (struct.output_ports ?? []).map(
    (port) => port.display_name || port.name,
  );

  return (
    <div className='w-full mb-4 px-3' data-testid='validate-mode-control'>
      <div className='flex items-center gap-1 mb-1 min-h-[36px]'>
        <Label className='text-sm'>If a check fails</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className='h-4 w-4 text-muted-foreground cursor-help' />
          </TooltipTrigger>
          <TooltipContent>
            <p className='max-w-xs'>
              Stop the run when you want a guard; pass the value through when a
              Switch or another node downstream should decide what to do with
              the errors.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div
        role='radiogroup'
        aria-label='If a check fails'
        className='grid grid-cols-1 gap-2'
      >
        {OPTIONS.map((option) => {
          const selected = config.mode === option.mode;
          const Icon = option.icon;
          return (
            <button
              key={option.mode}
              type='button'
              role='radio'
              aria-checked={selected}
              disabled={disabled}
              data-testid={`validate-mode-${option.mode}`}
              className={cn(
                'flex items-start gap-2 rounded-md border p-2 text-left transition-colors',
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/40',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
              onClick={() => !selected && onChange('mode', option.mode)}
            >
              <Icon
                className={cn(
                  'size-4 mt-0.5 shrink-0',
                  selected ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              <span className='min-w-0'>
                <span className='block text-xs font-medium'>
                  {option.title}
                </span>
                <span className='block text-[11px] text-muted-foreground'>
                  {option.body}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {config.mode === 'fail' ? (
        <div className='mt-2 grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1'>
          <span className='text-[11px] text-muted-foreground'>
            failure code
          </span>
          <DraftInput
            value={config.errorCode}
            disabled={disabled}
            aria-label='Failure code'
            className={cn(
              'font-mono',
              errorAt('error_code') && 'border-red-500',
            )}
            data-testid='validate-error-code'
            onCommit={(code) => onChange('error_code', code.trim())}
          />
          <span className='text-[11px] text-muted-foreground'>message</span>
          <DraftInput
            value={config.errorMessage}
            disabled={disabled}
            aria-label='Failure message'
            className={cn(errorAt('error_message') && 'border-red-500')}
            data-testid='validate-error-message'
            onCommit={(message) => onChange('error_message', message)}
          />
          {(errorAt('error_code') || errorAt('error_message')) && (
            <p className='col-span-2 text-[11px] text-red-500'>
              {errorAt('error_code') ?? errorAt('error_message')}
            </p>
          )}
        </div>
      ) : (
        <p className='mt-2 text-[11px] text-muted-foreground'>
          Outputs: {outputNames.join(', ') || 'Value, Is Valid, Errors'}.
          Connect &quot;Is Valid&quot; to a Switch to route on the outcome.
        </p>
      )}
    </div>
  );
}
